import React, { useMemo, forwardRef } from 'react';
import Button from '@atlaskit/button';
import Icon from '@atlaskit/icon';
import { Box, Inline, Stack, xcss, type BackgroundColor } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
	DROPDOWN,
} from '@atlassian/jira-product-analytics-bridge';
import { Link } from '@atlassian/react-resource-router';
import { AutomationGlyph } from '../../common/assets/automation.tsx';
import { AUTOMATION_RECOMMENDATIONS_PREFIX, PACKAGE_NAME } from '../../common/constants.tsx';
import type { TemplateRecommendation } from '../../common/types.tsx';
import { SOURCE_NAME } from './constants.tsx';
import { ExplainerSection } from './explainer-section/index.tsx';
import messages from './messages.tsx';
import { TemplateSection } from './template-section/index.tsx';
import { findHighestPriorityTemplate } from './utils.tsx';

type Props = {
	baseAutomationUrl: string;
	templates: TemplateRecommendation[];
	setClosed: () => void;
};

// atlaskit button passes type="button" to the component you specify which makes it render a button, this prevents that
const ForceLink = forwardRef((props: React.ComponentProps<typeof Link>, ref) => (
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
	<Link ref={ref as any} {...props} type="a" />
));

const UnsafeAutomationRecommendation = ({ baseAutomationUrl, templates, setClosed }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const template = useMemo(() => findHighestPriorityTemplate(templates), [templates]);

	if (!template) return null;

	const handleBuildRuleClick = () => {
		const analyticsEvent = createAnalyticsEvent({ action: 'clicked', actionSubject: 'link' });
		fireUIAnalytics(analyticsEvent, `${AUTOMATION_RECOMMENDATIONS_PREFIX}.buildRuleLink`, {
			templateId: template.id.value,
		});
		setClosed();
	};

	const handleTemplatesClick = () => {
		const analyticsEvent = createAnalyticsEvent({ action: 'clicked', actionSubject: 'link' });
		fireUIAnalytics(analyticsEvent, `${AUTOMATION_RECOMMENDATIONS_PREFIX}.templatesLink`, {
			templateId: template.id.value,
		});
		setClosed();
	};

	const ruleUrl = `${baseAutomationUrl}#/rule/template?templateId=${template.id.value}`;

	return (
		<ContextualAnalyticsData
			sourceName={SOURCE_NAME}
			sourceType={DROPDOWN}
			attributes={{ templateId: template.id.value }}
		>
			<FireScreenAnalytics />
			<Box
				padding="space.250"
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				backgroundColor={'color.background.information' as BackgroundColor}
				xcss={styles}
			>
				<Stack space="space.200">
					<ExplainerSection templateId={template.id.value} />
					<TemplateSection template={template} />
					<Inline alignBlock="center" space="space.150">
						<Button
							appearance="primary"
							spacing="compact"
							iconBefore={
								<Icon
									size="medium"
									glyph={AutomationGlyph}
									label=""
									primaryColor={token('color.icon.inverse')}
									secondaryColor="none"
								/>
							}
							component={ForceLink}
							href={ruleUrl}
							onClick={handleBuildRuleClick}
						>
							{formatMessage(messages.createRule)}
						</Button>
						<Link href={`${baseAutomationUrl}#/tab/rule-library#`} onClick={handleTemplatesClick}>
							{formatMessage(messages.viewTemplates)}
						</Link>
					</Inline>
				</Stack>
			</Box>
		</ContextualAnalyticsData>
	);
};

export const AutomationRecommendation = (props: Props) => (
	<JSErrorBoundary
		id={`${AUTOMATION_RECOMMENDATIONS_PREFIX}.panel`}
		packageName={PACKAGE_NAME}
		fallback="unmount"
	>
		<UnsafeAutomationRecommendation {...props} />
	</JSErrorBoundary>
);
const styles = xcss({ whiteSpace: 'normal' });
